import React, { useState, useEffect, useRef } from 'react';
import { 
  Printer, Package, Calendar, TrendingUp, FileText, 
  Download, RefreshCw, ChevronDown, Check, X,
  DollarSign, ShoppingCart, Clock, BarChart3, FileDown
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import html2pdf from 'html2pdf.js';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PrintCenter = ({ onBack, user }) => {
  const [activeSection, setActiveSection] = useState('products');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [products, setProducts] = useState([]);
  const [salesData, setSalesData] = useState(null);
  const [reportPeriod, setReportPeriod] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const printRef = useRef(null);

  const token = localStorage.getItem('auth_token');

  // Fetch products in stock
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      const data = await res.json();
      // Filter to only show in-stock products
      const inStockProducts = data.filter(p => p.in_stock !== false && p.quantity > 0);
      setProducts(inStockProducts);
      toast.success(`Loaded ${inStockProducts.length} products in stock`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch sales report
  const fetchSalesReport = async (period) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const today = new Date(selectedDate);
      
      if (period === 'daily') {
        params.append('start_date', selectedDate);
        params.append('end_date', selectedDate);
      } else if (period === 'weekly') {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        params.append('start_date', weekStart.toISOString().split('T')[0]);
        params.append('end_date', weekEnd.toISOString().split('T')[0]);
      } else if (period === 'monthly') {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        params.append('start_date', monthStart.toISOString().split('T')[0]);
        params.append('end_date', monthEnd.toISOString().split('T')[0]);
      }

      const res = await fetch(`${API_URL}/api/admin/reports/sales?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch sales report');
      const data = await res.json();
      setSalesData(data);
      toast.success(`Loaded ${period} sales report`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'products') {
      fetchProducts();
    } else {
      fetchSalesReport(reportPeriod);
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== 'products') {
      fetchSalesReport(reportPeriod);
    }
  }, [reportPeriod, selectedDate]);

  // Print function
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>UPLANDS FOOD MARKET - ${activeSection === 'products' ? 'Products Report' : 'Sales Report'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              padding: 20px;
              color: #1f2937;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px;
              border-bottom: 3px solid #0a1628;
              padding-bottom: 20px;
            }
            .header h1 { 
              font-size: 28px; 
              color: #0a1628;
              margin-bottom: 5px;
            }
            .header p { 
              color: #6b7280; 
              font-size: 14px;
            }
            .report-title {
              font-size: 20px;
              font-weight: bold;
              margin: 20px 0 10px;
              color: #0a1628;
            }
            .report-date {
              color: #6b7280;
              font-size: 12px;
              margin-bottom: 20px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 30px;
              font-size: 12px;
            }
            th { 
              background: #0a1628; 
              color: white; 
              padding: 12px 8px;
              text-align: left;
              font-weight: 600;
            }
            td { 
              padding: 10px 8px; 
              border-bottom: 1px solid #e5e7eb;
            }
            tr:nth-child(even) { background: #f9fafb; }
            tr:hover { background: #f3f4f6; }
            .summary-box {
              background: #f3f4f6;
              padding: 20px;
              border-radius: 8px;
              margin-bottom: 20px;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 15px;
            }
            .summary-item {
              text-align: center;
            }
            .summary-item .label {
              font-size: 11px;
              color: #6b7280;
              text-transform: uppercase;
            }
            .summary-item .value {
              font-size: 24px;
              font-weight: bold;
              color: #0a1628;
            }
            .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 11px;
              color: #9ca3af;
              border-top: 1px solid #e5e7eb;
              padding-top: 20px;
            }
            .stock-badge {
              display: inline-block;
              padding: 2px 8px;
              border-radius: 4px;
              font-size: 10px;
              font-weight: 600;
            }
            .stock-high { background: #dcfce7; color: #166534; }
            .stock-medium { background: #fef3c7; color: #92400e; }
            .stock-low { background: #fee2e2; color: #991b1b; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Export to PDF function
  const handleExportPDF = async () => {
    const printContent = printRef.current;
    if (!printContent) return;

    setExporting(true);
    toast.info('Generating PDF...');

    try {
      const filename = activeSection === 'products' 
        ? `UPLANDS_Products_In_Stock_${new Date().toISOString().split('T')[0]}.pdf`
        : `UPLANDS_${reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)}_Sales_Report_${selectedDate}.pdf`;

      const opt = {
        margin: [10, 10, 10, 10],
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: activeSection === 'products' ? 'landscape' : 'portrait'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(printContent).save();
      toast.success(`PDF saved as ${filename}`);
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStockLevel = (qty) => {
    if (qty >= 10) return { label: 'High', class: 'stock-high' };
    if (qty >= 5) return { label: 'Medium', class: 'stock-medium' };
    return { label: 'Low', class: 'stock-low' };
  };

  const getPeriodLabel = () => {
    const today = new Date(selectedDate);
    if (reportPeriod === 'daily') {
      return formatDate(selectedDate);
    } else if (reportPeriod === 'weekly') {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return `${weekStart.toLocaleDateString('en-CA')} - ${weekEnd.toLocaleDateString('en-CA')}`;
    } else {
      return today.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
    }
  };

  // Products Print Content
  const ProductsPrintContent = () => (
    <div>
      <div className="header">
        <h1>UPLANDS FOOD MARKET</h1>
        <p>Your Neighborhood African & Caribbean Grocery Store</p>
      </div>
      
      <div className="report-title">Products In Stock Report</div>
      <div className="report-date">Generated: {new Date().toLocaleString()}</div>
      
      <div className="summary-box">
        <div className="summary-grid">
          <div className="summary-item">
            <div className="label">Total Products</div>
            <div className="value">{products.length}</div>
          </div>
          <div className="summary-item">
            <div className="label">Total Units</div>
            <div className="value">{products.reduce((sum, p) => sum + (p.quantity || 0), 0)}</div>
          </div>
          <div className="summary-item">
            <div className="label">Total Value</div>
            <div className="value">{formatCurrency(products.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 0)), 0))}</div>
          </div>
          <div className="summary-item">
            <div className="label">Low Stock Items</div>
            <div className="value">{products.filter(p => (p.quantity || 0) < 5).length}</div>
          </div>
        </div>
      </div>
      
      <table>
        <thead>
          <tr>
            <th style={{width: '5%'}}>#</th>
            <th style={{width: '30%'}}>Product Name</th>
            <th style={{width: '15%'}}>Category</th>
            <th style={{width: '12%'}}>Barcode</th>
            <th className="text-right" style={{width: '10%'}}>Price</th>
            <th className="text-center" style={{width: '10%'}}>Qty</th>
            <th className="text-center" style={{width: '10%'}}>Stock</th>
            <th className="text-right" style={{width: '12%'}}>Value</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => {
            const stockLevel = getStockLevel(product.quantity || 0);
            return (
              <tr key={product.id}>
                <td>{index + 1}</td>
                <td>{product.name}</td>
                <td style={{textTransform: 'capitalize'}}>{product.category}</td>
                <td style={{fontFamily: 'monospace', fontSize: '11px'}}>{product.barcode || '-'}</td>
                <td className="text-right">{formatCurrency(product.price)}</td>
                <td className="text-center">{product.quantity || 0}</td>
                <td className="text-center">
                  <span className={`stock-badge ${stockLevel.class}`}>{stockLevel.label}</span>
                </td>
                <td className="text-right">{formatCurrency((product.price || 0) * (product.quantity || 0))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      
      <div className="footer">
        <p>UPLANDS FOOD MARKET | Merivale Road | Report ID: INV-{Date.now()}</p>
      </div>
    </div>
  );

  // Sales Print Content
  const SalesPrintContent = () => (
    <div>
      <div className="header">
        <h1>UPLANDS FOOD MARKET</h1>
        <p>Your Neighborhood African & Caribbean Grocery Store</p>
      </div>
      
      <div className="report-title">
        {reportPeriod === 'daily' ? 'Daily' : reportPeriod === 'weekly' ? 'Weekly' : 'Monthly'} Sales Report
      </div>
      <div className="report-date">
        Period: {getPeriodLabel()} | Generated: {new Date().toLocaleString()}
      </div>
      
      {salesData && (
        <>
          <div className="summary-box">
            <div className="summary-grid">
              <div className="summary-item">
                <div className="label">Total Orders</div>
                <div className="value">{salesData.total_orders || 0}</div>
              </div>
              <div className="summary-item">
                <div className="label">Total Revenue</div>
                <div className="value">{formatCurrency(salesData.total_revenue)}</div>
              </div>
              <div className="summary-item">
                <div className="label">Avg. Order Value</div>
                <div className="value">{formatCurrency(salesData.average_order_value)}</div>
              </div>
              <div className="summary-item">
                <div className="label">Items Sold</div>
                <div className="value">{salesData.total_items_sold || 0}</div>
              </div>
            </div>
          </div>
          
          {/* Order Type Breakdown */}
          <h3 style={{marginTop: '20px', marginBottom: '10px', fontSize: '16px', fontWeight: 'bold'}}>
            Sales by Order Type
          </h3>
          <table>
            <thead>
              <tr>
                <th>Order Type</th>
                <th className="text-center">Orders</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {salesData.by_order_type && Object.entries(salesData.by_order_type).map(([type, data]) => (
                <tr key={type}>
                  <td style={{textTransform: 'capitalize'}}>{type.replace('_', ' ')}</td>
                  <td className="text-center">{data.count || 0}</td>
                  <td className="text-right">{formatCurrency(data.revenue)}</td>
                  <td className="text-right">
                    {salesData.total_revenue > 0 
                      ? ((data.revenue / salesData.total_revenue) * 100).toFixed(1) 
                      : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Payment Method Breakdown */}
          <h3 style={{marginTop: '20px', marginBottom: '10px', fontSize: '16px', fontWeight: 'bold'}}>
            Sales by Payment Method
          </h3>
          <table>
            <thead>
              <tr>
                <th>Payment Method</th>
                <th className="text-center">Orders</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {salesData.by_payment_method && Object.entries(salesData.by_payment_method).map(([method, data]) => (
                <tr key={method}>
                  <td style={{textTransform: 'capitalize'}}>{method.replace('_', ' ')}</td>
                  <td className="text-center">{data.count || 0}</td>
                  <td className="text-right">{formatCurrency(data.revenue)}</td>
                  <td className="text-right">
                    {salesData.total_revenue > 0 
                      ? ((data.revenue / salesData.total_revenue) * 100).toFixed(1) 
                      : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Status Breakdown */}
          <h3 style={{marginTop: '20px', marginBottom: '10px', fontSize: '16px', fontWeight: 'bold'}}>
            Orders by Status
          </h3>
          <table>
            <thead>
              <tr>
                <th>Status</th>
                <th className="text-center">Count</th>
                <th className="text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {salesData.by_status && Object.entries(salesData.by_status).map(([status, data]) => (
                <tr key={status}>
                  <td style={{textTransform: 'capitalize'}}>{status.replace(/_/g, ' ')}</td>
                  <td className="text-center">{data.count || 0}</td>
                  <td className="text-right">{formatCurrency(data.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      
      <div className="footer">
        <p>UPLANDS FOOD MARKET | Merivale Road | Report ID: SALES-{Date.now()}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton onClick={onBack} />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Printer className="w-7 h-7 text-blue-600" />
                Print Center
              </h1>
              <p className="text-gray-500 text-sm">Generate and print reports</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleExportPDF}
              variant="outline"
              className="gap-2 border-red-300 text-red-600 hover:bg-red-50"
              disabled={loading || exporting}
              data-testid="pdf-button"
            >
              <FileDown className="w-4 h-4" />
              {exporting ? 'Generating...' : 'Save as PDF'}
            </Button>
            <Button 
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              disabled={loading || exporting}
              data-testid="print-button"
            >
              <Printer className="w-4 h-4" />
              Print Report
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {/* Report Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card 
            className={`p-4 cursor-pointer transition-all ${
              activeSection === 'products' 
                ? 'ring-2 ring-blue-500 bg-blue-50' 
                : 'hover:bg-gray-50'
            }`}
            onClick={() => setActiveSection('products')}
            data-testid="products-report-card"
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${activeSection === 'products' ? 'bg-blue-500' : 'bg-gray-100'}`}>
                <Package className={`w-6 h-6 ${activeSection === 'products' ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Products In Stock</h3>
                <p className="text-sm text-gray-500">All products with inventory details</p>
              </div>
              {activeSection === 'products' && <Check className="w-5 h-5 text-blue-500 ml-auto" />}
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${
              activeSection === 'sales' && reportPeriod === 'daily'
                ? 'ring-2 ring-green-500 bg-green-50' 
                : 'hover:bg-gray-50'
            }`}
            onClick={() => { setActiveSection('sales'); setReportPeriod('daily'); }}
            data-testid="daily-sales-card"
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${activeSection === 'sales' && reportPeriod === 'daily' ? 'bg-green-500' : 'bg-gray-100'}`}>
                <Calendar className={`w-6 h-6 ${activeSection === 'sales' && reportPeriod === 'daily' ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Daily Sales Report</h3>
                <p className="text-sm text-gray-500">Sales for a specific day</p>
              </div>
              {activeSection === 'sales' && reportPeriod === 'daily' && <Check className="w-5 h-5 text-green-500 ml-auto" />}
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${
              activeSection === 'sales' && (reportPeriod === 'weekly' || reportPeriod === 'monthly')
                ? 'ring-2 ring-purple-500 bg-purple-50' 
                : 'hover:bg-gray-50'
            }`}
            onClick={() => { setActiveSection('sales'); setReportPeriod('weekly'); }}
            data-testid="period-sales-card"
          >
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${activeSection === 'sales' && (reportPeriod === 'weekly' || reportPeriod === 'monthly') ? 'bg-purple-500' : 'bg-gray-100'}`}>
                <TrendingUp className={`w-6 h-6 ${activeSection === 'sales' && (reportPeriod === 'weekly' || reportPeriod === 'monthly') ? 'text-white' : 'text-gray-600'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Weekly/Monthly Sales</h3>
                <p className="text-sm text-gray-500">Extended period sales reports</p>
              </div>
              {activeSection === 'sales' && (reportPeriod === 'weekly' || reportPeriod === 'monthly') && <Check className="w-5 h-5 text-purple-500 ml-auto" />}
            </div>
          </Card>
        </div>

        {/* Controls */}
        {activeSection === 'sales' && (
          <Card className="p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Period</label>
                <div className="flex gap-2">
                  {['daily', 'weekly', 'monthly'].map((period) => (
                    <Button
                      key={period}
                      variant={reportPeriod === period ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setReportPeriod(period)}
                      className={reportPeriod === period ? 'bg-blue-600' : ''}
                      data-testid={`period-${period}`}
                    >
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="date-selector"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchSalesReport(reportPeriod)}
                disabled={loading}
                className="mt-6"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </Card>
        )}

        {activeSection === 'products' && (
          <Card className="p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="secondary" className="text-lg py-1 px-3">
                  {products.length} Products
                </Badge>
                <Badge variant="outline" className="text-lg py-1 px-3">
                  {products.reduce((sum, p) => sum + (p.quantity || 0), 0)} Total Units
                </Badge>
                <Badge className="bg-green-100 text-green-800 text-lg py-1 px-3">
                  {formatCurrency(products.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 0)), 0))} Value
                </Badge>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchProducts}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </Card>
        )}

        {/* Preview Area */}
        <Card className="p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Print Preview
            </h2>
            {loading && (
              <div className="flex items-center gap-2 text-gray-500">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading...
              </div>
            )}
          </div>
          
          <div 
            ref={printRef}
            className="border border-gray-200 rounded-lg p-6 bg-white max-h-[600px] overflow-auto"
            style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
          >
            {activeSection === 'products' ? (
              <ProductsPrintContent />
            ) : (
              <SalesPrintContent />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrintCenter;
